// ============================================================================
// Guided path overview (spec §6.1): units → lessons with progress, lock/unlock,
// and completion. A lesson is "complete" once every item has been studied; the
// next lesson unlocks when the previous one is complete (first is always open).
// ============================================================================

import { el, mount } from '../dom.js';
import * as api from '@core/data/api.js';

const isComplete = (l) => l.item_count > 0 && l.studied_count >= l.item_count;

export function renderPath({ navigate }) {
  const container = el('div.container.stack');
  mount(container, el('div.card', { text: 'Loading your path…' }));

  (async () => {
    let rows;
    try {
      rows = await api.curriculum();
    } catch (e) {
      return mount(container, el('div.card.banner.banner--warn', { text: e.message || 'Could not load the path' }));
    }
    if (!rows.length) {
      return mount(container, el('div.card', { text: 'No curriculum yet.' }));
    }

    // rows are ordered by unit then lesson; unlock = previous lesson complete.
    let prevComplete = true;
    rows.forEach((l) => {
      l._unlocked = prevComplete || isComplete(l);
      prevComplete = isComplete(l);
    });

    // Group into units (preserving order).
    const units = [];
    const byId = new Map();
    for (const l of rows) {
      if (!byId.has(l.unit_id)) {
        const u = { id: l.unit_id, title: l.unit_title, order: l.unit_order, lessons: [] };
        byId.set(l.unit_id, u); units.push(u);
      }
      byId.get(l.unit_id).lessons.push(l);
    }

    const doneCount = rows.filter(isComplete).length;
    const nextLesson = rows.find((l) => l._unlocked && !isComplete(l));

    const header = el('div.card.stack', {}, [
      el('h1', { text: 'Guided path' }),
      el('p.muted', { text: `${doneCount} of ${rows.length} lessons complete. Learn characters and the words they build, in order.` }),
      nextLesson
        ? el('button.btn.btn--primary', { text: `Continue → ${nextLesson.unit_title}, ${nextLesson.lesson_title}`, onclick: () => navigate(`#/lesson/${nextLesson.lesson_id}`) })
        : el('p.muted', { text: 'All lessons complete — 축하합니다! 🎉' }),
    ]);

    const unitCards = units.map((u) => {
      const done = u.lessons.filter(isComplete).length;
      const lessonRows = u.lessons.map((l) => {
        const complete = isComplete(l);
        const locked = !l._unlocked;
        const pct = l.item_count ? Math.round((l.studied_count / l.item_count) * 100) : 0;
        const status = complete ? '✓' : locked ? '🔒' : `${l.studied_count}/${l.item_count}`;
        const row = el(`button.card.lesson-row${locked ? '.lesson-row--locked' : ''}`, {
          disabled: locked,
          onclick: () => !locked && navigate(`#/lesson/${l.lesson_id}`),
        }, [
          el('div.lesson-row__main', {}, [
            el('div.row', {}, [
              el('strong', { text: l.lesson_title }),
              complete ? el('span.badge-state.state-mastered', { text: 'done' }) : false,
            ]),
            el('div.faint', { text: l.objective || '' }),
          ]),
          el('div.lesson-row__status', { text: status }),
        ]);
        if (!locked && !complete && pct > 0) {
          row.append(el('div.progressbar', {}, [el('div.progressbar__fill', { style: `width:${pct}%` })]));
        }
        return row;
      });
      return el('div.card.stack', {}, [
        el('div.row', {}, [el('h2', { text: u.title }), el('div.appbar__spacer'), el('span.faint', { text: `${done}/${u.lessons.length}` })]),
        el('div.stack', { style: 'gap:var(--space-2)' }, lessonRows),
      ]);
    });

    mount(container, header, ...unitCards);
  })();

  return container;
}
